import React from 'react';
import {
    BarChart,
    Bar,
    Cell,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
} from 'recharts';
import { useTranslation } from 'react-i18next';

type Props = {
    weeklyData: any[];
    weeklyTotal: number;
    colors: any;
};

const HomeWeeklyActivity: React.FC<Props> = ({
    weeklyData,
    weeklyTotal,
    colors,
}) => {
    const { t } = useTranslation();

    const hasRealData = weeklyData && weeklyData.length > 0;

    // Se NON ci sono dati, creiamo una barra finta con valore 5
    // per far vedere almeno una colonna.
    // Trasformiamo i dati per la visualizzazione
    const chartData = React.useMemo(() => {
        if (!hasRealData) {
            return [{ name: '', value: 5, visualValue: 5 }];
        }
        return weeklyData.map((d: any) => ({
            ...d,
            visualValue: d.value >= 10 ? 10 : d.value === 0 ? 0.3 : d.value,
        }));
    }, [weeklyData, hasRealData]);

    return (
        <div className="rounded-3xl bg-white shadow-sm p-6 md:p-8 h-fit relative overflow-hidden">
            <div className="flex justify-between items-start mb-8 relative z-10">
                <div>
                    <h3 className="text-xl font-bold text-gray-900">
                        {t('home.weekly_activity')}
                    </h3>
                    <p className="text-sm text-gray-400 mt-1">
                        {t('home.confirmed_bookings')}
                    </p>
                </div>
                <div className="text-right">
                    <span className="block text-3xl font-bold text-clip-600">
                        {hasRealData ? weeklyTotal : 0}
                    </span>
                    <span className="text-xs text-gray-400 uppercase tracking-wider font-semibold">
                        {t('home.total')}
                    </span>
                </div>
            </div>

            <div className="h-64 relative z-10">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                        <defs>
                            <linearGradient
                                id="vibrantGradient"
                                x1="0"
                                y1="0"
                                x2="0"
                                y2="1"
                            >
                                <stop
                                    offset="0%"
                                    stopColor={colors.primary}
                                    stopOpacity={1}
                                />
                                <stop
                                    offset="100%"
                                    stopColor={colors.charts.gradientEnd}
                                    stopOpacity={0.6}
                                />
                            </linearGradient>
                        </defs>
                        <YAxis hide domain={[0, 10]} />
                        <XAxis
                            dataKey="name"
                            axisLine={false}
                            tickLine={false}
                            tick={
                                hasRealData
                                    ? {
                                        fill: colors.text.light,
                                        fontSize: 12,
                                        fontWeight: 500,
                                    }
                                    : { fill: 'transparent' } // niente label se è la barra fake
                            }
                            dy={10}
                        />
                        <Tooltip
                            cursor={{ fill: `${colors.primary}1A`, radius: 8 }}
                            content={({ active, payload, label }) => {
                                if (!active) return null;

                                // NESSUN DATO REALE: tooltip testuale, niente value=2, ecc.
                                if (!hasRealData) {
                                    return (
                                        <div className="rounded-lg bg-white shadow-lg px-3 py-2 text-xs text-gray-700 max-w-[200px]">
                                            {t('home.weekly_empty_state')}
                                        </div>
                                    );
                                }

                                if (!payload || !payload.length) return null;
                                const value = payload[0].payload.value as number;

                                return (
                                    <div className="rounded-lg bg-white shadow-lg px-3 py-2 text-xs text-gray-700">
                                        <div className="font-semibold mb-1">
                                            {t('home.tooltip_day_label')}: {label}
                                        </div>
                                        <div>
                                            {t('home.tooltip_bookings_label')}: {value}
                                        </div>
                                    </div>
                                );
                            }}
                        />
                        <Bar
                            dataKey="visualValue"
                            radius={[12, 12, 12, 12]}
                            barSize={24}
                            animationDuration={2000}
                        >
                            {chartData.map((entry: any, index: number) => (
                                <Cell
                                    key={`cell-${index}`}
                                    fill="url(#vibrantGradient)"
                                    className="hover:opacity-80 transition-opacity"
                                />
                            ))}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};

export default HomeWeeklyActivity;